// src/pages/finance-portal/loans/LoanDetail.js
//
// Loan detail with header KPIs, amortization schedule, covenants.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, IconButton, Card, Chip, Alert, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip,
    MenuItem, Select, FormControl, InputLabel, InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GavelIcon from '@mui/icons-material/Gavel';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const STATUS_COLORS = {
    ACTIVE:     { bg: '#dcfce7', color: '#166534' },
    PAID_OFF:   { bg: '#e0e7ff', color: '#4338ca' },
    DEFAULTED:  { bg: '#fecaca', color: '#991b1b' },
    REFINANCED: { bg: '#fef9c3', color: '#854d0e' },
    CLOSED:     { bg: '#f1f5f9', color: '#475569' },
};

const PAYMENT_STATUS_COLORS = {
    SCHEDULED: { bg: '#f1f5f9', color: '#64748b' },
    PAID:      { bg: '#dcfce7', color: '#166534' },
    PARTIAL:   { bg: '#fef9c3', color: '#854d0e' },
    OVERDUE:   { bg: '#fecaca', color: '#991b1b' },
    SKIPPED:   { bg: '#f1f5f9', color: '#94a3b8' },
};

const COVENANT_STATUS_COLORS = {
    COMPLIANT:  { bg: '#dcfce7', color: '#166534' },
    AT_RISK:    { bg: '#fef9c3', color: '#854d0e' },
    BREACH:     { bg: '#fecaca', color: '#991b1b' },
    NOT_TESTED: { bg: '#f1f5f9', color: '#64748b' },
};

const COVENANT_OP_LABEL = {
    GTE: '≥', LTE: '≤', GT: '>', LT: '<', EQ: '=',
};

function fmtMoney(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: 2,
    }).format(val || 0);
}

function emptyCovenant() {
    return {
        name: '', metric: '', operator: 'LTE', threshold_value: '',
        warning_threshold_value: '', test_frequency: 'QUARTERLY',
        next_test_date: '', description: '',
    };
}

export default function LoanDetail() {
    const { loanId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [loan, setLoan] = useState(null);
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [payDialog, setPayDialog] = useState({
        open: false, payment: null,
        paid_date: '', actual_principal_paid: '', actual_interest_paid: '',
        actual_fees_paid: '', payment_reference: '', notes: '',
    });

    const [covDialog, setCovDialog] = useState({
        open: false, mode: 'create', editId: null, ...emptyCovenant(),
    });

    const [testDialog, setTestDialog] = useState({
        open: false, covenant: null, test_date: '', actual_value: '', notes: '',
    });

    const fetchLoan = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/loans/${loanId}`);
            if (res.data.success) setLoan(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [loanId]);

    useEffect(() => { fetchLoan(); }, [fetchLoan]);

    // ---- Schedule actions ----
    const regenerate = async () => {
        if (!window.confirm('Regenerate the entire schedule? Only allowed if no payments are recorded.')) return;
        try {
            const res = await axiosInstance.post(`/api/finance/loans/${loanId}/regenerate-schedule`);
            enqueueSnackbar(res.data?.message || 'Schedule regenerated', { variant: 'success' });
            fetchLoan();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const closeLoan = async () => {
        try {
            await axiosInstance.post(`/api/finance/loans/${loanId}/close`);
            enqueueSnackbar('Loan closed', { variant: 'success' });
            fetchLoan();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const openPay = (payment) => setPayDialog({
        open: true, payment,
        paid_date: new Date().toISOString().slice(0, 10),
        actual_principal_paid: String(payment.scheduled_principal),
        actual_interest_paid: String(payment.scheduled_interest),
        actual_fees_paid: '0',
        payment_reference: '', notes: '',
    });

    const submitPay = async () => {
        try {
            const res = await axiosInstance.post(
                `/api/finance/loans/${loanId}/payments/${payDialog.payment.id}/record`,
                {
                    paid_date: payDialog.paid_date,
                    actual_principal_paid: parseFloat(payDialog.actual_principal_paid) || 0,
                    actual_interest_paid: parseFloat(payDialog.actual_interest_paid) || 0,
                    actual_fees_paid: parseFloat(payDialog.actual_fees_paid) || 0,
                    payment_reference: payDialog.payment_reference || null,
                    notes: payDialog.notes || null,
                },
            );
            enqueueSnackbar(res.data?.message || 'Payment recorded', { variant: 'success' });
            setPayDialog({ ...payDialog, open: false });
            fetchLoan();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    // ---- Covenant actions ----
    const openCovCreate = () => setCovDialog({ open: true, mode: 'create', editId: null, ...emptyCovenant() });
    const openCovEdit = (c) => setCovDialog({
        open: true, mode: 'edit', editId: c.id,
        name: c.name || '', metric: c.metric || '', operator: c.operator || 'LTE',
        threshold_value: String(c.threshold_value ?? ''),
        warning_threshold_value: c.warning_threshold_value != null ? String(c.warning_threshold_value) : '',
        test_frequency: c.test_frequency || 'QUARTERLY',
        next_test_date: c.next_test_date || '',
        description: c.description || '',
    });

    const submitCovenant = async () => {
        const payload = {
            name: covDialog.name?.trim(),
            description: covDialog.description?.trim() || null,
            metric: covDialog.metric?.trim(),
            operator: covDialog.operator,
            threshold_value: parseFloat(covDialog.threshold_value),
            warning_threshold_value: covDialog.warning_threshold_value === ''
                ? null : parseFloat(covDialog.warning_threshold_value),
            test_frequency: covDialog.test_frequency,
            next_test_date: covDialog.next_test_date || null,
        };
        try {
            if (covDialog.mode === 'edit') {
                await axiosInstance.put(
                    `/api/finance/loans/${loanId}/covenants/${covDialog.editId}`, payload);
                enqueueSnackbar('Covenant updated', { variant: 'success' });
            } else {
                await axiosInstance.post(`/api/finance/loans/${loanId}/covenants`, payload);
                enqueueSnackbar('Covenant created', { variant: 'success' });
            }
            setCovDialog({ ...covDialog, open: false });
            fetchLoan();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const deleteCovenant = async (c) => {
        if (!window.confirm(`Delete covenant "${c.name}"?`)) return;
        try {
            await axiosInstance.delete(`/api/finance/loans/${loanId}/covenants/${c.id}`);
            enqueueSnackbar('Covenant deleted', { variant: 'success' });
            fetchLoan();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const openTest = (covenant) => setTestDialog({
        open: true, covenant,
        test_date: new Date().toISOString().slice(0, 10),
        actual_value: '', notes: '',
    });

    const submitTest = async () => {
        try {
            const res = await axiosInstance.post(
                `/api/finance/loans/${loanId}/covenants/${testDialog.covenant.id}/tests`,
                {
                    test_date: testDialog.test_date,
                    actual_value: parseFloat(testDialog.actual_value),
                    notes: testDialog.notes || null,
                },
            );
            enqueueSnackbar(res.data?.message || 'Test recorded', { variant: 'success' });
            setTestDialog({ ...testDialog, open: false });
            fetchLoan();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', bgcolor: '#f8fafc',
                        borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    if (loading) return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;
    if (!loan) return (
        <Box sx={{ p: 4, fontFamily }}>
            <Alert severity="error">{error || 'Loan not found'}</Alert>
            <Button onClick={() => navigate('/finance-portal/loans')} sx={{ mt: 2, fontFamily }}>
                Back to list
            </Button>
        </Box>
    );

    const sStyle = STATUS_COLORS[loan.status] || STATUS_COLORS.ACTIVE;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/loans')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            {loan.loan_number} · {loan.lender}
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {loan.name}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Chip label={loan.status?.replace('_', ' ')} size="small"
                          sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                bgcolor: sStyle.bg, color: sStyle.color }} />
                    <Chip label={loan.loan_type?.replace('_', ' ')} size="small"
                          sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem',
                                bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                    <Chip label={`${loan.interest_rate}% ${loan.rate_type}`} size="small"
                          sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem',
                                bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1300, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* KPIs */}
                <Box sx={{ display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                          gap: 2, mb: 3 }}>
                    {[
                        { label: 'Original', value: fmtMoney(loan.original_amount, loan.currency), color: '#0f172a' },
                        { label: 'Current Balance',
                          value: fmtMoney(loan.current_balance, loan.currency),
                          color: loan.current_balance > 0 ? '#dc2626' : '#16a34a' },
                        { label: 'Total Principal Paid',
                          value: fmtMoney(loan.total_paid_principal, loan.currency), color: '#16a34a' },
                        { label: 'Total Interest Paid',
                          value: fmtMoney(loan.total_paid_interest, loan.currency), color: '#0ea5e9' },
                    ].map(k => (
                        <Card key={k.label} sx={{ borderRadius: '14px', boxShadow: 'none',
                                                   border: '1px solid #e2e8f0', p: 2.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                              textTransform: 'uppercase', fontWeight: 600 }}>
                                {k.label}
                            </Typography>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.3rem', color: k.color, mt: 0.25 }}>
                                {k.value}
                            </Typography>
                        </Card>
                    ))}
                </Box>

                {/* Action bar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {loan.status !== 'CLOSED' && (
                        <Button startIcon={<EditIcon />} variant="outlined" size="small"
                                onClick={() => navigate(`/finance-portal/loans/${loan.id}/edit`)}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                            Edit
                        </Button>
                    )}
                    {loan.status !== 'CLOSED' && (
                        <Tooltip title="Wipe & rebuild the entire amortization schedule">
                            <span>
                                <Button startIcon={<RefreshIcon />} variant="outlined" size="small"
                                        onClick={regenerate}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                    Regenerate Schedule
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {loan.status === 'ACTIVE' && (
                        <Button startIcon={<LockIcon />} variant="text" size="small" onClick={closeLoan}
                                sx={{ fontFamily, textTransform: 'none', color: '#94a3b8' }}>
                            Close Loan
                        </Button>
                    )}
                </Box>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem' },
                    '& .Mui-selected': { color: '#22c55e' },
                    '& .MuiTabs-indicator': { bgcolor: '#22c55e', height: 3 } }}>
                    <Tab label={`Schedule (${loan.payments?.length || 0})`} />
                    <Tab label={`Covenants (${loan.covenants?.length || 0})`} />
                </Tabs>

                {tab === 0 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={labelCell}>#</TableCell>
                                        <TableCell sx={labelCell}>Due</TableCell>
                                        <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Sched. Principal</TableCell>
                                        <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Sched. Interest</TableCell>
                                        <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Sched. Total</TableCell>
                                        <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Bal. After</TableCell>
                                        <TableCell sx={labelCell}>Status</TableCell>
                                        <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Paid</TableCell>
                                        <TableCell sx={labelCell}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(loan.payments || []).length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4,
                                                ...dataCell, color: '#94a3b8' }}>
                                                No schedule
                                            </TableCell>
                                        </TableRow>
                                    ) : loan.payments.map(p => {
                                        const ps = PAYMENT_STATUS_COLORS[p.status] || PAYMENT_STATUS_COLORS.SCHEDULED;
                                        return (
                                            <TableRow key={p.id} hover>
                                                <TableCell sx={dataCell}>{p.payment_number}</TableCell>
                                                <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>{p.due_date}</TableCell>
                                                <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                    {fmtMoney(p.scheduled_principal, loan.currency)}
                                                </TableCell>
                                                <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                    {fmtMoney(p.scheduled_interest, loan.currency)}
                                                </TableCell>
                                                <TableCell sx={{ ...dataCell, textAlign: 'right',
                                                                  fontWeight: 600, color: '#1e293b' }}>
                                                    {fmtMoney(p.scheduled_total, loan.currency)}
                                                </TableCell>
                                                <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                    {fmtMoney(p.scheduled_balance_after, loan.currency)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={p.status} size="small"
                                                          sx={{ fontFamily, fontSize: '0.7rem', height: 20,
                                                                bgcolor: ps.bg, color: ps.color }} />
                                                </TableCell>
                                                <TableCell sx={{ ...dataCell, textAlign: 'right',
                                                                  color: p.actual_total_paid > 0 ? '#16a34a' : '#94a3b8' }}>
                                                    {p.actual_total_paid > 0 ? fmtMoney(p.actual_total_paid, loan.currency) : '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {p.status !== 'PAID' && loan.status !== 'CLOSED' && (
                                                        <Button size="small" startIcon={<PaymentIcon />}
                                                                onClick={() => openPay(p)}
                                                                sx={{ fontFamily, textTransform: 'none', fontSize: '0.78rem' }}>
                                                            Record
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}

                {tab === 1 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0',
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>
                                Loan Covenants
                            </Typography>
                            {loan.status !== 'CLOSED' && (
                                <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={openCovCreate}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                    Add Covenant
                                </Button>
                            )}
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={labelCell}>Name</TableCell>
                                        <TableCell sx={labelCell}>Metric</TableCell>
                                        <TableCell sx={labelCell}>Threshold</TableCell>
                                        <TableCell sx={labelCell}>Frequency</TableCell>
                                        <TableCell sx={labelCell}>Next Test</TableCell>
                                        <TableCell sx={labelCell}>Last Actual</TableCell>
                                        <TableCell sx={labelCell}>Status</TableCell>
                                        <TableCell sx={labelCell}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(loan.covenants || []).length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4,
                                                ...dataCell, color: '#94a3b8' }}>
                                                No covenants tracked. Add one to monitor compliance.
                                            </TableCell>
                                        </TableRow>
                                    ) : loan.covenants.map(c => {
                                        const cs = COVENANT_STATUS_COLORS[c.current_status] || COVENANT_STATUS_COLORS.NOT_TESTED;
                                        return (
                                            <TableRow key={c.id} hover>
                                                <TableCell sx={{ ...dataCell, color: '#1e293b', fontWeight: 500 }}>
                                                    {c.name}
                                                </TableCell>
                                                <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                    {c.metric}
                                                </TableCell>
                                                <TableCell sx={dataCell}>
                                                    {COVENANT_OP_LABEL[c.operator] || c.operator} {c.threshold_value}
                                                    {c.warning_threshold_value != null && (
                                                        <Typography component="span" sx={{ fontFamily, fontSize: '0.7rem',
                                                            color: '#94a3b8', ml: 0.5 }}>
                                                            (warn @ {c.warning_threshold_value})
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                    {c.test_frequency}
                                                </TableCell>
                                                <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                    {c.next_test_date || '—'}
                                                </TableCell>
                                                <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                    {c.last_test_actual != null
                                                        ? `${c.last_test_actual} on ${c.last_test_date || '—'}` : '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={c.current_status?.replace('_', ' ')} size="small"
                                                          sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                                                height: 22, bgcolor: cs.bg, color: cs.color }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="Record test">
                                                            <IconButton size="small" onClick={() => openTest(c)}
                                                                        sx={{ color: '#3b82f6' }}>
                                                                <GavelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => openCovEdit(c)}
                                                                        sx={{ color: '#64748b' }}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => deleteCovenant(c)}
                                                                        sx={{ color: '#ef4444' }}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}
            </Box>

            {/* Record-payment dialog */}
            <Dialog open={payDialog.open} onClose={() => setPayDialog({ ...payDialog, open: false })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Record Payment {payDialog.payment ? `#${payDialog.payment.payment_number}` : ''}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                        <TextField label="Paid Date *" type="date" size="small"
                                   value={payDialog.paid_date}
                                   onChange={e => setPayDialog(p => ({ ...p, paid_date: e.target.value }))}
                                   InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <TextField label="Principal Paid *" size="small" type="number" fullWidth
                                       value={payDialog.actual_principal_paid}
                                       onChange={e => setPayDialog(p => ({ ...p, actual_principal_paid: e.target.value }))}
                                       InputProps={{
                                           startAdornment: <InputAdornment position="start"
                                               sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                           sx: { fontFamily, fontSize: '0.85rem' },
                                       }}
                                       InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                            <TextField label="Interest Paid *" size="small" type="number" fullWidth
                                       value={payDialog.actual_interest_paid}
                                       onChange={e => setPayDialog(p => ({ ...p, actual_interest_paid: e.target.value }))}
                                       InputProps={{
                                           startAdornment: <InputAdornment position="start"
                                               sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                           sx: { fontFamily, fontSize: '0.85rem' },
                                       }}
                                       InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        </Box>
                        <TextField label="Fees" size="small" type="number"
                                   value={payDialog.actual_fees_paid}
                                   onChange={e => setPayDialog(p => ({ ...p, actual_fees_paid: e.target.value }))}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: { fontFamily, fontSize: '0.85rem' },
                                   }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Reference (check #, ACH ID)" size="small"
                                   value={payDialog.payment_reference}
                                   onChange={e => setPayDialog(p => ({ ...p, payment_reference: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Notes" size="small" multiline rows={2}
                                   value={payDialog.notes}
                                   onChange={e => setPayDialog(p => ({ ...p, notes: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPayDialog({ ...payDialog, open: false })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitPay}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#22c55e',
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        Record
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Covenant create/edit dialog */}
            <Dialog open={covDialog.open} onClose={() => setCovDialog({ ...covDialog, open: false })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {covDialog.mode === 'edit' ? 'Edit Covenant' : 'New Covenant'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                        <TextField label="Name *" size="small"
                                   value={covDialog.name}
                                   onChange={e => setCovDialog(p => ({ ...p, name: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Metric *" size="small"
                                   placeholder="e.g. Debt-to-Equity, Min EBITDA"
                                   value={covDialog.metric}
                                   onChange={e => setCovDialog(p => ({ ...p, metric: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <FormControl size="small" sx={{ width: 130 }}>
                                <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Operator</InputLabel>
                                <Select value={covDialog.operator} label="Operator"
                                        onChange={e => setCovDialog(p => ({ ...p, operator: e.target.value }))}
                                        sx={{ fontFamily, borderRadius: '10px' }}>
                                    {Object.entries(COVENANT_OP_LABEL).map(([k, v]) =>
                                        <MenuItem key={k} value={k} sx={{ fontFamily }}>{v} ({k})</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField label="Threshold *" size="small" type="number" fullWidth
                                       value={covDialog.threshold_value}
                                       onChange={e => setCovDialog(p => ({ ...p, threshold_value: e.target.value }))}
                                       InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                       InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                            <TextField label="Warn at" size="small" type="number" fullWidth
                                       value={covDialog.warning_threshold_value}
                                       onChange={e => setCovDialog(p => ({ ...p, warning_threshold_value: e.target.value }))}
                                       helperText="Optional"
                                       InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                       InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                       FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <FormControl size="small" fullWidth>
                                <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Frequency</InputLabel>
                                <Select value={covDialog.test_frequency} label="Frequency"
                                        onChange={e => setCovDialog(p => ({ ...p, test_frequency: e.target.value }))}
                                        sx={{ fontFamily, borderRadius: '10px' }}>
                                    {['MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL'].map(f =>
                                        <MenuItem key={f} value={f} sx={{ fontFamily }}>{f.replace('_', ' ')}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField label="Next Test Date" type="date" size="small" fullWidth
                                       value={covDialog.next_test_date}
                                       onChange={e => setCovDialog(p => ({ ...p, next_test_date: e.target.value }))}
                                       InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.85rem' } }}
                                       InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        </Box>
                        <TextField label="Description" size="small" multiline rows={2}
                                   value={covDialog.description}
                                   onChange={e => setCovDialog(p => ({ ...p, description: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCovDialog({ ...covDialog, open: false })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitCovenant}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#22c55e',
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test recording dialog */}
            <Dialog open={testDialog.open} onClose={() => setTestDialog({ ...testDialog, open: false })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Record Test — {testDialog.covenant?.name}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 1.5, fontFamily, fontSize: '0.78rem', borderRadius: '10px' }}>
                        Threshold: {COVENANT_OP_LABEL[testDialog.covenant?.operator] || testDialog.covenant?.operator}{' '}
                        {testDialog.covenant?.threshold_value}
                    </Alert>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField label="Test Date *" type="date" size="small"
                                   value={testDialog.test_date}
                                   onChange={e => setTestDialog(p => ({ ...p, test_date: e.target.value }))}
                                   InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Actual Value *" size="small" type="number"
                                   value={testDialog.actual_value}
                                   onChange={e => setTestDialog(p => ({ ...p, actual_value: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Notes" size="small" multiline rows={2}
                                   value={testDialog.notes}
                                   onChange={e => setTestDialog(p => ({ ...p, notes: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setTestDialog({ ...testDialog, open: false })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitTest}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#22c55e',
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        Record
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
